import { useEffect, useRef, useState } from 'react';
import type { EmailTemplate } from '@epasu/core';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RichTextArea } from '@/components/RichTextArea';

interface Props {
  /** null = neue Vorlage */
  template: EmailTemplate | null;
  onClose: () => void;
  /**
   * Bekommt die gespeicherte Vorlage. Wer den Dialog nur zum Pflegen oeffnet
   * (Einstellungen), ignoriert sie; der Schritte-Reiter der Kampagne waehlt
   * die frisch verfasste damit gleich aus.
   */
  onSaved: (template: EmailTemplate) => void;
}

export function TemplateDialog({ template, onClose, onSaved }: Props) {
  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '{{anrede}},\n\n');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !subject.trim()) {
      setError('Name und Betreff werden gebraucht.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const gespeichert = template
        ? await crm.templates.update(template.id, { name, subject, body })
        : await crm.templates.create({ name, subject, body });
      // `update` liefert `null`, wenn die Vorlage inzwischen weg ist — dann
      // gibt es nichts auszuwaehlen, aber der Dialog schliesst trotzdem.
      onSaved(gespeichert ?? template!);
    } catch (err) {
      // Der häufigste Fall ist ein bereits vergebener Name — der Index in der
      // Datenbank verhindert Dubletten.
      setError(
        err instanceof Error && /UNIQUE/i.test(err.message)
          ? 'Eine Vorlage mit diesem Namen gibt es bereits.'
          : err instanceof Error
            ? err.message
            : 'Speichern fehlgeschlagen',
      );
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={template ? 'Vorlage bearbeiten' : 'Neue Vorlage'}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="my-auto w-full max-w-2xl rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold">
          {template ? 'Vorlage bearbeiten' : 'Neue Vorlage'}
        </h2>

        <div className="mt-4 space-y-3">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Name</span>
            <Input
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Angebot nachfassen"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Betreff</span>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </label>

          <RichTextArea label="Text" value={body} onChange={setBody} rows={16} />

          <p className="text-xs text-muted-foreground">
            Platzhalter: <code>{'{{anrede}}'}</code> ergibt „Guten Tag Anna Meier“
            oder, wenn kein Name bekannt ist, „Sehr geehrte Damen und Herren“.
            <code>{'{{csv_anrede}}'}</code> nimmt die Anrede, die am Kontakt hinterlegt
            ist — etwa „Liebes Team des Neuen Gymnasiums“ aus dem CSV-Import — und
            ohne sie dasselbe wie <code>{'{{anrede}}'}</code>.{' '}
            <code>{'{{anrede_persoenlich}}'}</code> nimmt die persönliche Anrede aus dem
            CSV-Import („Sehr geehrter Herr Rehn“), sonst „Guten Tag Frau Hamm“, wenn am
            Kontakt Herr oder Frau und ein Nachname stehen — sonst dasselbe wie{' '}
            <code>{'{{csv_anrede}}'}</code>.
            Dazu <code>{'{{vorname}}'}</code>, <code>{'{{nachname}}'}</code> und{' '}
            <code>{'{{firma}}'}</code>.
          </p>
        </div>

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Speichere …' : 'Speichern'}
          </Button>
        </div>
      </form>
    </div>
  );
}
